<script lang="ts">
	import Basic from '../templates/Basic.svelte'
	import AutocompleteInputField from '../autocomplete/AutocompleteInputField.svelte'
	import DatePickerInput from '../atoms/DatePickerInput.svelte'
	import { t } from '$i18n/i18n'
	import { setContext } from 'svelte'
	import { DatePickerMode } from '$types/datepicker'
	import { inputFieldFormats } from '$utils/formatters'
	import {
		type CardInfos,
		type DashboardPage,
		type DashboardPerson,
		type DashCardParams
	} from './DashboardPage.model.js'
	import DashCard from './dashCard/DashCard.svelte'
	import { jwt } from '$utils/jwt'
	import dayjs from 'dayjs'
	import { writable, type Writable } from 'svelte/store'
	import {
		type AutocompleteFieldConfig,
		DefaultAutocompleteConfig
	} from '$types/autocompleteFieldConfig'
	import {
		extractUsernameFromJwt,
		fillCardParamsFromUrlOrDefault,
		updateUrlWithSearchParams
	} from './Dashboard.controller'
	import MediaQuery from '../templates/MediaQuery.svelte'
	import SecondaryButton, { SecondaryBtnColors } from '../atoms/SecondaryButton.svelte'
	import { ButtonIcons } from '$types/enums'
	import { exportDashboardToExcel } from './Dashboard.controller.js'

	/** context stores */
	setContext('useMaterialDesign', true)
	const cardParams: Writable<DashCardParams> = setContext(
		'cardQueryData',
		writable({ ...fillCardParamsFromUrlOrDefault(new URLSearchParams(window.location.search)) })
	)

	const ALL_PERSONS_KEY = 'allPersons'
	let defaultSelectionId: string
	let defaultIsInitialized: boolean = false

	/** autocomplete field configs */
	const pagesAutocompleteConfig: AutocompleteFieldConfig = DefaultAutocompleteConfig()
	pagesAutocompleteConfig.valueField = 'publicId'
	pagesAutocompleteConfig.labelField = 'title'
	pagesAutocompleteConfig.valueAsObject = true
	pagesAutocompleteConfig.fetch = '/api/dashboard/pages'
	pagesAutocompleteConfig.fetchResetOnBlur = true
	pagesAutocompleteConfig.searchProps = {
		sort: {
			field: 'priority',
			direction: 'asc'
		},
		skipSort: false,
		fields: ['title', 'text'],
		disabled: false
	}

	const personsAutocompleteConfig: AutocompleteFieldConfig = DefaultAutocompleteConfig()
	personsAutocompleteConfig.valueField = 'id'
	personsAutocompleteConfig.fetch = '/api/dashboard/persons'
	personsAutocompleteConfig.fetchResetOnBlur = true
	personsAutocompleteConfig.multiple = true
	personsAutocompleteConfig.fetchCallback = (response) => groupOptions(response)
	personsAutocompleteConfig.groupItemsField = 'options'
	personsAutocompleteConfig.groupLabelField = 'label'

	/* local variables */
	let dashboardPage: DashboardPage | null = null
	let dashboardPageId: string | null = null
	let cardInfos: CardInfos[] = []
	let username: string | null = null
	let userId: string[] | null = null

	let prevUserSelection: string[] = []

	/*  functions */
	function updateCardInfos() {
		cardInfos = [...(dashboardPage?.cardInfos ?? [])]
		updateUrlWithSearchParams(dashboardPageId, $cardParams, window)
	}

	/**
	 * A fetch callback that groups the options by their type (single or team)
	 * @param response the response from the fetch request including all the options(teams and persons),
	 * teams only have a text and id, persons have a name, lastname, salutation, text and id
	 */
	function groupOptions(response: { data?: DashboardPerson[] } | null | undefined) {
		let options: DashboardPerson[] = [...(response?.data ?? [])]
		let teams: DashboardPerson[] = [
			...options.filter((opt) => opt.id.includes(',') || opt?.id === ALL_PERSONS_KEY)
		]
		let persons: DashboardPerson[] = [
			...options.filter((opt) => !opt.id.includes(',') && opt?.id !== ALL_PERSONS_KEY)
		]
		if (teams.length === 0 && persons.length === 1) {
			defaultSelectionId = persons[0].id
		} else {
			defaultSelectionId = ALL_PERSONS_KEY
		}
		return [
			{
				label: 'Teams',
				options: teams
			},
			{
				label: 'Personen',
				options: persons
			}
		]
	}

	function initLowestPrioPageAndLoadCards(options: DashboardPage[] | null | undefined) {
		let sortedList: DashboardPage[] = options?.sort((a, b) => a.priority - b.priority) || []
		const urlSearchParams = new URLSearchParams(window.location.search)
		const pageId = urlSearchParams.get('pageId')
		if (pageId) {
			dashboardPage = sortedList.find((page) => page?.publicId === pageId) || sortedList[0]
		} else {
			dashboardPage = sortedList[0]
		}
		updateCardInfos()
	}

	function updateCardParams() {
		prevUserSelection = [...$cardParams.userId]
		if (userId !== null && userId?.length > 0) {
			// when initially all persons are selected, we want to remove the 'allPersons' selection after selecting a person or team
			if (
				prevUserSelection?.length === 1 &&
				prevUserSelection[0] === ALL_PERSONS_KEY &&
				defaultIsInitialized
			) {
				userId = [...userId.filter((id) => id !== ALL_PERSONS_KEY)]
			}
			// when selecting 'allPersons', we want to remove all other selections
			if (!prevUserSelection.includes(ALL_PERSONS_KEY) && userId.includes(ALL_PERSONS_KEY)) {
				userId = [ALL_PERSONS_KEY]
			}
			defaultIsInitialized = true // we need this flag to prevent removing of the 'allPersons' when we read from the url to init the values
			$cardParams.userId = userId
		} else {
			$cardParams.userId = defaultSelectionId ? [defaultSelectionId] : []
		}
	}

	function exportDashboard() {
		exportDashboardToExcel(
			dashboardPageId,
			userId,
			$cardParams.mainTimeframe,
			$cardParams.prevTimeframe
		)
	}

	function setDefaultSelectionValue() {
		if (defaultSelectionId) {
			// we need this, otherwise the autocomplete field is not ready and does not select the default option
			setTimeout(() => {
				userId = [defaultSelectionId]
				updateCardParams()
			}, 0)
		}
	}

	/* reactivity and subscriptions */

	jwt.subscribe((jwt) => {
		username = extractUsernameFromJwt(jwt)
	})

	cardParams.subscribe((params) => {
		updateUrlWithSearchParams(dashboardPageId, params, window)
		userId = params.userId
	})

	$: dashboardPageId = dashboardPage?.publicId ?? null
	$: if (defaultSelectionId) {
		setDefaultSelectionValue()
	}
</script>

<Basic showLoadingAnimation={false} maxWidth="2160px" backgroundColor="#f5f5f5">
	<div class="dashboard dashboard__background">
		<div class="dashboard__header">
			<div class="dashboard__header--info">
				<h1>{$t('UI.Dashboard.dashboard')}</h1>
				<h1>{dayjs().format('DD.MM.YYYY')}</h1>
			</div>
			<div class="dashboard__header--salutation-and-export">
				<div class="dashboard__header--salutation">
					<h1>{$t('UI.Dashboard.salutation', { uname: username ?? '' })}</h1>
					<!-- I used the if here on purpose to not have the user input render in the html tag -->
					{#if dashboardPage?.text}
						<h4>{dashboardPage.text}</h4>
					{:else}
						<h4>{@html $t('UI.Dashboard.info')}</h4>
					{/if}
				</div>
				<MediaQuery query="screen and (max-width: 767px)" let:matches>
					{#if matches}
						<div class="dashboard__header--export">
							<SecondaryButton
								iconSize="24px"
								iconPadding="8px"
								leftIcon={ButtonIcons.SHARE}
								color={SecondaryBtnColors.GREEN}
								displayIconOnly
								on:click={exportDashboard}
							/>
						</div>
					{/if}
				</MediaQuery>
			</div>
		</div>
		<div class="dashboard__filter">
			<div>
				<AutocompleteInputField
					bind:value={dashboardPage}
					label={$t('UI.Dashboard.dashboardPage.label')}
					autocompleteConfig={pagesAutocompleteConfig}
					hideDropdownArrow={false}
					onChange={updateCardInfos}
					onFetch={initLowestPrioPageAndLoadCards}
				/>
				<DatePickerInput
					bind:date={$cardParams.mainTimeframe}
					label={$t('UI.Dashboard.mainTimeframe.label')}
					mode={DatePickerMode.RANGE}
					format={inputFieldFormats.FULL}
				/>
				<DatePickerInput
					bind:date={$cardParams.prevTimeframe}
					label={$t('UI.Dashboard.prevTimeframe.label')}
					mode={DatePickerMode.RANGE}
					format={inputFieldFormats.FULL}
				/>
				<!-- we don't bind on the cardParams store here immediately, because when clearing the field, we want it
                to default to 'allPersons', therefore, we have to intercept the changes and propagate them manually in the
                 cardParams -->
				<AutocompleteInputField
					bind:value={userId}
					label={$t('UI.Dashboard.person.label')}
					autocompleteConfig={personsAutocompleteConfig}
					hideDropdownArrow={false}
					onChange={updateCardParams}
				/>
			</div>
			<MediaQuery query="screen and (min-width: 768px)" let:matches>
				{#if matches}
					<div class="dashboard__header--export">
						<SecondaryButton
							iconSize="24px"
							iconPadding="8px"
							leftIcon={ButtonIcons.SHARE}
							color={SecondaryBtnColors.GREEN}
							titleAttr={$t('UI.Dashboard.export.help')}
							displayIconOnly
							on:click={exportDashboard}
						/>
					</div>
				{/if}
			</MediaQuery>
		</div>
		<div class="dashboard__cards-grid">
			{#each cardInfos as cardInfo (cardInfo.publicId)}
				<DashCard cardId={cardInfo.publicId} cardTitle={cardInfo.title} />
			{/each}
		</div>
	</div>
</Basic>

<style lang="scss">
	@use '$styles/global';
	@use '$styles/breakpoints';

	/* set background for body such that success message does not have white background when showing */
	body {
		--bs-body-bg: #f5f5f5;
	}

	/* place notifications absolute such that the content is not shifting when the notification appears */
	:global(#main-notification-container) {
		top: 60px; /* height of top bar */
		position: fixed;
	}

	.dashboard {
		@include global.flex-col($justify: flex-start);
	}

	.dashboard__header {
		width: 100%;

		@include global.flex-col;
	}

	.dashboard__header--info {
		width: 100%;

		@include global.flex-row(0.75rem, $justify: space-between);

		margin-bottom: 1.875rem;

		h1 {
			color: global.$primaryGreen;
			font-size: 0.825rem;
			margin-bottom: 0;
		}
	}

	.dashboard__header--salutation-and-export {
		width: 100%;

		@include global.flex-row(0.75rem, $justify: space-between, $alignment: flex-end);

		margin-bottom: 0.625rem;
	}

	.dashboard__header--salutation {
		& h1 {
			@include global.roboto-font(normal, 600, 1.25rem, #000);
		}

		& h4 {
			font-size: 0.875rem;
			line-height: 1.375rem;
			margin-bottom: 0;
		}
	}

	.dashboard__filter {
		@include global.flex-row(0.625rem, $justify: space-between);

		width: 100%;
		margin-bottom: 0.625rem;

		& > div:first-child {
			width: 100%;
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 0.625rem 0.75rem;
		}

		& :global(.material-label.notEmpty .material-text),
		& :global(.material-label.isFocused .material-text),
		& :global(.input-field-focus.date-time-field.notEmpty ~ .material-label .material-text) {
			background: linear-gradient(to bottom, #f5f5f5, white);
		}
	}

	.dashboard__cards-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 0.625rem;
		width: 100%;
	}

	@media screen and (min-width: breakpoints.$small) {
		.dashboard {
			height: calc(100vh - 60px - 50px - 2.5rem); // subtract menu bar, bottom bar, padding top
		}

		.dashboard__background {
			background: url('@/images/dashboard-background.png') no-repeat right 2rem;
			background-size: 50%;
		}

		.dashboard__header--salutation-and-export {
			margin-bottom: 1.25rem;
		}

		.dashboard__header--info {
			h1 {
				color: global.$primaryGreen;
				font-size: 1.25rem;
			}
		}

		.dashboard__header--salutation {
			& h1 {
				font-size: 2rem;
			}

			& h4 {
				font-size: 1.25rem;
				line-height: 1.75rem;
			}
		}

		.dashboard__filter {
			margin-bottom: 1.25rem;

			& > div:first-child {
				width: 100%;
				max-width: 1020px; /* the single dropdown fields should not be longer than 255px */

				@include global.flex-row(0.625rem, $justify: flex-start);
			}
		}

		.dashboard__cards-grid {
			grid-template-columns: repeat(2, 1fr);
			gap: 1.25rem;
		}
	}

	@media screen and (min-width: breakpoints.$medium) {
		.dashboard__cards-grid {
			grid-template-columns: repeat(3, 1fr);
		}
	}

	@media screen and (min-width: breakpoints.$xlarge) {
		.dashboard__cards-grid {
			grid-template-columns: repeat(4, 1fr);
		}
	}
</style>
